import { useGetLocale, useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { Button, Flex, Tag, Typography, theme } from 'antd';
import type { SetFilters } from '@/utils/filters';
import {
  activeFilterChips,
  chipsToClear,
  defaultChipValueLabel,
} from '@/utils/activeFilters';
import type { ChipScalar, ChipValue, FilterChip } from '@/utils/activeFilters';

export interface ActiveFiltersProps {
  filters: CrudFilter[];
  setFilters: SetFilters;
  /** Translation namespace holding this resource's `fields.*` labels. */
  labelPrefix: string;
  /** Fields whose control is already on screen — the search box's `q` by default. */
  ignore?: string[];
  /** Fields labelled from the shared `base.fields.*` namespace instead. */
  baseFields?: string[];
  /**
   * Label for a field that lives outside both namespaces; return `undefined`
   * to fall back to `labelPrefix` / `base`.
   */
  formatField?: (field: string) => string | undefined;
  /**
   * Enum values carry per-resource translations; return `undefined` to let the
   * value print itself (numbers as-is, ISO days in the user's locale).
   */
  formatValue?: (field: string, value: ChipScalar) => string | undefined;
  /** Told which field was dropped, e.g. to clear a now-stale view label. */
  onRemove?: (field: string) => void;
}

/**
 * The active filters, one removable chip each, plus a "clear" action.
 *
 * A table's own dropdowns only say *that* a column is filtered, and filters
 * that arrive in the URL say nothing at all — this is where the filter set
 * becomes readable.
 */
export const ActiveFilters = ({
  filters,
  setFilters,
  labelPrefix,
  ignore = ['q'],
  baseFields = [],
  formatField,
  formatValue,
  onRemove,
}: ActiveFiltersProps) => {
  const t = useTranslate();
  const locale = useGetLocale()();
  const { token } = theme.useToken();

  const chips = activeFilterChips(filters, ignore);
  if (chips.length === 0) return null;

  const fieldLabel = (field: string) =>
    formatField?.(field) ??
    (baseFields.includes(field)
      ? t(`base.fields.${field}`)
      : t(`${labelPrefix}.fields.${field}`));

  const one = (field: string, value: ChipScalar) =>
    formatValue?.(field, value) ?? defaultChipValueLabel(value, locale);

  const valueLabel = (field: string, value: ChipValue) => {
    switch (value.kind) {
      case 'value':
        return one(field, value.value);
      case 'values': {
        const list = value.values.map((v) => one(field, v)).join(', ');
        return value.excluded ? `≠ ${list}` : list;
      }
      case 'range':
        return `${value.from != null ? one(field, value.from) : '…'} – ${
          value.to != null ? one(field, value.to) : '…'
        }`;
      case 'presence':
        return t(`base.filter.${value.present ? 'present' : 'absent'}`);
    }
  };

  const remove = (chip: FilterChip) => {
    setFilters(
      chip.remove.map((r) => ({ ...r, value: undefined })),
      'merge'
    );
    onRemove?.(chip.field);
  };

  const clearAll = () => {
    setFilters(chipsToClear(chips), 'merge');
    chips.forEach((c) => onRemove?.(c.field));
  };

  return (
    <Flex
      align="center"
      gap={token.marginXXS}
      wrap
      style={{ marginBottom: token.marginSM }}
    >
      {chips.map((chip) => (
        <Tag
          key={chip.id}
          color="processing"
          closable
          onClose={() => remove(chip)}
          style={{ marginInlineEnd: 0, paddingBlock: token.paddingXXS }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 'inherit' }}>
            {fieldLabel(chip.field)}:{' '}
          </Typography.Text>
          {valueLabel(chip.field, chip.value)}
        </Tag>
      ))}
      <Button type="link" size="small" onClick={clearAll}>
        {t('actions.clear')}
      </Button>
    </Flex>
  );
};
